import { Signal, signal } from '@angular/core';
import { FieldTree, max, min, required, SchemaPathTree } from '@angular/forms/signals';
import { match, Option } from '../../../shared/functions/functional';
import { ACHVEarthingDto, ACLVEarthingDto, DCEarthingDto, EarthingDto } from '../../../core/mceapi.client.generated';
import { appOpt, getNoneOptionDto, getSomeOptionDto } from '../../../core/mceapi.client.helpers';

export interface Earthing {
  case: string,
  dcCase: string,
  aclvCase: string,
  achvCase: string,
  soilResistivity: number;
};

export const dcCaseSelect = signal([
  { value: 'mid_point', label: 'Mid-Point' },
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
  { value: 'isolated', label: 'Isolated' }
]);

export const aclvCaseSelect = signal([
  { value: 'tns', label: 'TNS' },
  { value: 'tncs', label: 'TNCS' },
  { value: 'tt', label: 'TT' },
  { value: 'it', label: 'IT' }
]);

export const achvCaseSelect = signal([
  { value: 'solidly_earthed', label: 'Solidly Earthed' },
  { value: 'resistance_earthed', label: 'Resistance Earthed' },
  { value: 'reactance_earthed', label: 'Reactance Earthed' }
]);

export function buildEarthingSchema(path: SchemaPathTree<Earthing>) {
  required(path.soilResistivity, { message: `Soil resistivity is required` });
  min(path.soilResistivity, 0, { message: `Soil resistivity must be positive` });
  max(path.soilResistivity, 100000, { message: `Soil resistivity must be less than or equal to 100,000 Ω.m` });
}


const getDcCase = (opt: Option<DCEarthingDto>) => match(() => 'negative', (x: DCEarthingDto) => x.case ?? 'negative')(opt);
const getAclvCase = (opt: Option<ACLVEarthingDto>) => match(() => 'tncs', (x: ACLVEarthingDto) => x.case ?? 'tncs')(opt);
const getAchvCase = (opt: Option<ACHVEarthingDto>) => match(() => 'solidly_earthed', (x: ACHVEarthingDto) => x.case ?? 'solidly_earthed')(opt);
const getRho = (earthing: EarthingDto): number => {
  switch (earthing.case) {
    case 'dc': return match(() => 100, (x: DCEarthingDto) => x.soilResistivity ?? 100)(appOpt(earthing.dc ?? {}));
    case 'aclv': return match(() => 100, (x: ACLVEarthingDto) => x.soilResistivity ?? 100)(appOpt(earthing.aclv ?? {}));
    case 'achv': return match(() => 100, (x: ACHVEarthingDto) => x.soilResistivity ?? 100)(appOpt(earthing.achv ?? {}));
    default: throw new Error(`Unsupported earthing case '${earthing.case}'.`);
  }
};

export function createEarthing(earthing: EarthingDto) {
  if (earthing.case !== 'dc' && earthing.case !== 'aclv' && earthing.case !== 'achv') {
    throw new Error(`Unsupported earthing case '${earthing.case}'.`);
  }

  return signal<Earthing>({
    case: earthing.case,
    dcCase: getDcCase(appOpt(earthing.dc ?? {})),
    aclvCase: getAclvCase(appOpt(earthing.aclv ?? {})),
    achvCase: getAchvCase(appOpt(earthing.achv ?? {})),
    soilResistivity: getRho(earthing)
  });
};

export function createEarthingDto(earthing: FieldTree<Earthing>): Signal<EarthingDto> {
  let base = {
    case: earthing.case().value(),
    dc: getNoneOptionDto<DCEarthingDto>(),
    aclv: getNoneOptionDto<ACLVEarthingDto>(),
    achv: getNoneOptionDto<ACHVEarthingDto>(),
  } as EarthingDto;

  switch (base.case) {
    case 'dc': return signal<EarthingDto>({
      ...base, dc: getSomeOptionDto<DCEarthingDto>({ case: earthing.dcCase().value(), soilResistivity: earthing.soilResistivity().value() })
    });
    case 'aclv': return signal<EarthingDto>({
      ...base, aclv: getSomeOptionDto<ACLVEarthingDto>({ case: earthing.aclvCase().value(), soilResistivity: earthing.soilResistivity().value() })
    });
    case 'achv': return signal<EarthingDto>({
      ...base, achv: getSomeOptionDto<ACHVEarthingDto>({ case: earthing.achvCase().value(), soilResistivity: earthing.soilResistivity().value() })
    });
  }

  throw new Error(`Unsupported earthing case '${base.case}'.`);
}
